import {
  ActivityIconColumn,
  ActivityIdFieldColumn,
  ActivityImageTitleColumn,
  ChapterImageTitleColumn,
  ChaptersColumn,
  CommentsFieldColumn,
  CreatedByFieldColumn,
  CreatedFieldColumn,
  DocumentNameColumn,
  DraftActivityImageTitleColumn,
  DraftActivityTitleColumn,
  DraftAdminDisplayNameColumn,
  DraftCreatedByFieldColumn,
  DraftToolTitleColumn,
  ImageActionsColumn,
  ImageCaptionColumn,
  CommentColumn,
  ImageDimensionsColumn,
  ImageFileNameColumn,
  ImageFileSizeColumn,
  ImageIdFieldColumn,
  ImageNameColumn,
  ImageTypeColumn,
  LinkUrlColumn,
  ModifiedByFieldColumn,
  ModifiedFieldColumn,
  PublishedActivityTitleColumn,
  PublishedAdminDisplayNameColumn,
  PublishedToolTitleColumn,
  ToolActivitiesColumn,
  ToolFileIconColumn,
  ToolIdFieldColumn,
  VersionFieldColumn,
  VersionIdFieldColumn,
  VsaTableColumnDefinition,
} from "../constants/DataGridColumns";
import { Activity } from "../models/Activity";
import { Field } from "../models/Field";
import { Image } from "../models/Image";
import { Tool, ToolTypes } from "../models/Tool";
import { getColumnForField } from "./GenericDataGridService";

export function getToolColumnsFromColumnOrder(
  standardFields: string[],
  fields: Field[],
  activityFields: Field[],
  fieldOrder: string[],
  toolType: ToolTypes,
  isDraft: boolean
): VsaTableColumnDefinition<Tool>[] {
  const columns: VsaTableColumnDefinition<Tool>[] = [];
  const isDocument = toolType === ToolTypes.Document;

  fieldOrder.forEach((fieldName) => {
    switch (fieldName) {
      case "toolId":
        columns.push(ToolIdFieldColumn);
        break;
      case "activities":
        columns.push(ToolActivitiesColumn);
        break;
      case "title": {
        const titleColumn = getTitleColumn(false, isDraft);
        if (titleColumn) {
          columns.push(titleColumn);
        }
        break;
      }
      case "type":
        columns.push(ToolFileIconColumn);
        break;
      case "name":
        columns.push(isDocument ? DocumentNameColumn : LinkUrlColumn);
        break;
      case "adminDisplayName":
        columns.push(
          isDraft
            ? DraftAdminDisplayNameColumn
            : PublishedAdminDisplayNameColumn
        );
        break;
      case "chapters":
        columns.push(ChaptersColumn);
        break;
      case "comments":
        columns.push(isDocument ? CommentsFieldColumn : CommentColumn);
        break;
      default: {
        let column = getStandardFieldOrFieldColumn<Tool>(
          fieldName,
          standardFields,
          fields,
          !isDocument
        );

        if (column) {
          columns.push(column);
        } else {
          column = getStandardFieldOrFieldColumn<Tool>(
            fieldName,
            standardFields,
            activityFields,
            !isDocument
          );

          if (column) {
            columns.push(column);
          }
        }
      }
    }
  });

  return columns;
}

export function getActivityColumnsFromColumnOrder(
  standardFields: string[],
  fields: Field[],
  fieldOrder: string[],
  isDraft: boolean
): VsaTableColumnDefinition<Activity>[] {
  const columns: VsaTableColumnDefinition<Activity>[] = [];

  fieldOrder.forEach((fieldName) => {
    switch (fieldName) {
      case "activityId":
        columns.push(ActivityIdFieldColumn);
        break;
      case "title": {
        const titleColumn = getTitleColumn(true, isDraft);
        if (titleColumn) {
          columns.push(titleColumn);
        }
        break;
      }
      case "type":
        columns.push(ActivityIconColumn);
        break;
      case "chapters":
        columns.push(ChaptersColumn);
        break;
      default: {
        const column = getStandardFieldOrFieldColumn<Activity>(
          fieldName,
          standardFields,
          fields,
          false
        );

        if (column) {
          columns.push(column);
        }
      }
    }
  });

  return columns;
}

function getStandardFieldOrFieldColumn<T extends Activity | Tool | Image>(
  fieldName: string,
  standardFields: string[],
  fields: Field[],
  hideBooleanField: boolean
) {
  if (standardFields.indexOf(fieldName) !== -1) {
    return getStandardFieldColumn<T>(fieldName);
  } else {
    const field = fields.find((f) => f.uniqueName === fieldName);

    if (field) {
      return getColumnForField(field, hideBooleanField);
    }
  }
}

function getStandardFieldColumn<T extends Activity | Tool | Image>(
  fieldName: string
): VsaTableColumnDefinition<T> | undefined {
  switch (fieldName) {
    case "versionId":
      return VersionIdFieldColumn;
    case "version":
      return VersionFieldColumn;
    case "modified":
      return ModifiedFieldColumn;
    case "modifiedBy":
      return ModifiedByFieldColumn;
    case "created":
      return CreatedFieldColumn;
    case "createdBy":
      return CreatedByFieldColumn;
    case "comments":
      return CommentsFieldColumn;
    case "draftCreatedBy":
      return DraftCreatedByFieldColumn;
  }
}

function getTitleColumn<T extends Activity | Tool>(
  isActivity: boolean,
  isDraft: boolean
): VsaTableColumnDefinition<T> | undefined {
  if (isActivity && isDraft) {
    return DraftActivityTitleColumn;
  } else if (isActivity && !isDraft) {
    return PublishedActivityTitleColumn;
  } else if (!isActivity && isDraft) {
    return DraftToolTitleColumn;
  } else if (!isActivity && !isDraft) {
    return PublishedToolTitleColumn;
  }
}

export function getImageColumnsFromColumnOrder(
  standardFields: string[],
  fields: Field[],
  activityFields: Field[],
  fieldOrder: string[],
  onEdit: (imageId: string) => void,
  isActivityContext: boolean = false,
  isDraftContext: boolean = false
): VsaTableColumnDefinition<Image>[] {
  const columns: VsaTableColumnDefinition<Image>[] = [];

  fieldOrder.forEach((fieldName) => {
    switch (fieldName) {
      case "imageId":
        columns.push(ImageIdFieldColumn);
        break;
      case "fileName":
        columns.push(ImageFileNameColumn);
        break;
      case "title":
        if (isActivityContext) {
          columns.push(
            isDraftContext
              ? DraftActivityImageTitleColumn
              : ActivityImageTitleColumn
          );
        } else {
          columns.push(ChapterImageTitleColumn);
        }
        break;
      case "name":
        columns.push(ImageNameColumn);
        break;
      case "type":
      case "fileType":
        columns.push(ImageTypeColumn);
        break;
      case "fileSize":
      case "fileSizeBytes":
      case "imageFileSize":
        columns.push(ImageFileSizeColumn);
        break;
      case "dimensions":
      case "pixelDimensions":
      case "imagePixelSize":
        columns.push(ImageDimensionsColumn);
        break;
      case "chapters":
        columns.push(ChaptersColumn);
        break;
      case "imageCaption":
        columns.push(ImageCaptionColumn);
        break;
      case "comments":
        columns.push(CommentColumn);
        break;
      case "actions":
        columns.push(ImageActionsColumn(onEdit));
        break;
      case "process": {
        const field = activityFields.find((f) => f.uniqueName === fieldName);
        if (field) {
          const column = getColumnForField(field, false);
          if (column) {
            columns.push(column);
          }
        }
        break;
      }
      default: {
        let column = getStandardFieldOrFieldColumn<Image>(
          fieldName,
          standardFields,
          fields,
          false
        );
        if (column) {
          columns.push(column);
        } else {
          column = getStandardFieldOrFieldColumn<Image>(
            fieldName,
            standardFields,
            activityFields,
            false
          );
          if (column) {
            columns.push(column);
          }
        }
      }
    }
  });

  return columns;
}
